import { Truck } from '@numeo/types-core'
import { FleetTruckCounts, FleetTruckMatch, FleetTruckMatchRequest, FleetTruckPage, FleetTruckQuery } from '@numeo/types-main-web-app'
import { requestApi } from './axiosBaseQuery'
import { baseApi } from './baseApi'
import { loadTruck } from './truckBatcher'

/**
 * The fleet's trucks, read from the server when a screen needs them: they are never part of the application `/auth/verify`
 * answers. Pages of at most 100 (`/fleet/trucks/v1`), single trucks by id (batched), counts and file matches. Every argument
 * carries the company's `applicationId`, so a cache entry never crosses companies. Read through the hooks in `hooks/fleet/`.
 */

export type FleetTrucksQuery = FleetTruckQuery

type CompanyArg = { applicationId: string }
export type ListFleetTrucksArg = FleetTrucksQuery & CompanyArg

/** The tag every page, count and match carries, so one truck change re-reads them all. */
export const FLEET_TRUCKS_LIST_ID = 'LIST'
/** The counts' own tag. */
export const FLEET_TRUCKS_COUNTS_ID = 'COUNTS'

const emptyPage = (query: FleetTrucksQuery): FleetTruckPage => ({ items: [], pagination: { total: 0, page: query.page ?? 1, limit: query.limit ?? 0 } })

export const fleetTrucksApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listFleetTrucks: build.query<FleetTruckPage, ListFleetTrucksArg>({
            queryFn: async (query, { signal }) => {
                // `applicationId` keys the cache only: the server answers for the token's company.
                const params = {
                    page: query.page,
                    limit: query.limit,
                    search: query.search,
                    ids: query.ids?.length ? query.ids.join(',') : undefined,
                    status: query.status && query.status !== 'all' ? query.status : undefined,
                    contractor: query.contractor,
                    driver: query.driver,
                    dispatcher: query.dispatcher,
                    eldIntegration: query.eldIntegration,
                    includeDeleted: query.includeDeleted && query.ids?.length ? 'true' : undefined,
                }
                const result = await requestApi<FleetTruckPage>({ url: '/fleet/trucks/v1', params }, signal)
                return 'error' in result ? result : { data: result.data ?? emptyPage(query) }
            },
            providesTags: (result) => [
                { type: 'Trucks', id: FLEET_TRUCKS_LIST_ID },
                ...(result?.items ?? []).map((truck) => ({ type: 'Trucks' as const, id: String(truck._id) })),
            ],
            // A truck is cached in one place by id too: the rows a page brings answer every by-id lookup of them.
            onQueryStarted: async ({ applicationId }, { dispatch, queryFulfilled }) => {
                const fulfilled = await queryFulfilled.catch(() => null) // the hook reports a failed page
                if (!fulfilled) return
                dispatch(
                    fleetTrucksApi.util.upsertQueryEntries(
                        fulfilled.data.items.map((truck) => ({
                            endpointName: 'getTruck' as const,
                            arg: { applicationId, id: String(truck._id) },
                            value: truck,
                        }))
                    )
                )
            },
        }),
        /** One truck by id (deleted ones too), batched with every id asked in the same tick. */
        getTruck: build.query<Truck | null, CompanyArg & { id: string }>({
            queryFn: ({ id }) => loadTruck(id),
            providesTags: (_result, _error, { id }) => [{ type: 'Trucks', id }],
        }),
        getFleetTruckCounts: build.query<FleetTruckCounts, CompanyArg>({
            queryFn: async (_arg, { signal }) => {
                const result = await requestApi<FleetTruckCounts>({ url: '/fleet/trucks/counts/v1' }, signal)
                if ('error' in result) return result
                return { data: result.data ?? { total: 0, active: 0, terminated: 0, manualWithoutEld: 0, byEldIntegration: {} } }
            },
            providesTags: [
                { type: 'Trucks', id: FLEET_TRUCKS_COUNTS_ID },
                { type: 'Trucks', id: FLEET_TRUCKS_LIST_ID },
            ],
        }),
        /** The trucks a file or a provider names (units, VINs, ELD vehicle ids), from the server. */
        matchFleetTrucks: build.query<Truck[], CompanyArg & FleetTruckMatchRequest>({
            queryFn: async ({ units, devices, plates, vins, vehicleIds }, { signal }) => {
                const body = { units, devices, plates, vins, vehicleIds }
                const result = await requestApi<FleetTruckMatch>({ url: '/fleet/trucks/match/v1', method: 'POST', body }, signal)
                return 'error' in result ? result : { data: result.data?.items ?? [] }
            },
            providesTags: [{ type: 'Trucks', id: FLEET_TRUCKS_LIST_ID }],
        }),
    }),
})

export const { useListFleetTrucksQuery, useGetFleetTruckCountsQuery, useMatchFleetTrucksQuery } = fleetTrucksApi
